<script setup lang="tsx">
  import { PencilIcon, TrashIcon, ChevronDownIcon } from '@heroicons/vue/solid';
  import { get, post } from '~/services/donationScreen';
  import type {
    AddNewExpenseAllocationDropdownApiResponse,
    AddNewExpenseFormState,
    AllocationBudgetOptions,
    AllocationGrantsOptions,
    GetExpenseResponse,
    ResponseStatus
  } from '~/types';
  import InfoRow from '~/components/UI/Drawers/InfoRow.vue';
  import InfoSection from '~/components/UI/Drawers/InfoSection.vue';
  import DrawerLayout from '~/components/UI/Drawers/DrawerLayout.vue';
  import { formatToIndianCurrency } from '~/functions/currencyUtilities';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import GrantsAndBudgetAllocation from '../RoutedDrawerComponents/GrantsAndBudgetAllocation.vue';
  import { expenseFormDefaultState, parseFormDataToState } from '~/components/Expenses/Forms/addNewExpenseUtils';
  import { cloneDeep, isEmpty } from 'lodash';
  import dayjs from 'dayjs';
  import Attachments from '../../Others/Attachments.vue';
  import AuditLogs from '~/components/shared/RoutedDrawers/RoutedDrawerComponents/AuditLogs.vue';
  import PaymentSummaryRow from '~/components/Expenses/Table/PaymentSummaryRow.vue';
  import { getPaymentsDetails, getPaymentSummary, sumConditional } from '~/components/Expenses/Table/expenseTableUtils';
  import ApproveExpenseModal from '../../Modals/ApproveExpenseModal.vue';
  import DenyExpenseModal from '../../Modals/DenyExpenseModal.vue';
  import CancelExpenseModal from '../../Modals/CancelExpenseModal.vue';
  import Approvals from '../RoutedDrawerComponents/Approvals.vue';
  import GrantAndBudgetAllocationLoadingState from '../../../UI/Drawers/GrantAndBudgetAllocationLoadingState.vue';

  const { onClose, identifier } = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const { role } = useAuth();
  const router = useRouter();

  const data = ref<AddNewExpenseFormState>(cloneDeep(expenseFormDefaultState));
  const responseState = ref<ResponseStatus>('success');
  const isExpenseCancelled = ref<boolean>(false);
  const activeKey = ref([]);
  const paymentAllocations = ref();
  const paymentsDetails = ref();
  const denyExpenseModal = ref<boolean>(false);
  const approveExpenseModal = ref<boolean>(false);
  const cancelExpenseModal = ref<boolean>(false);
  const approvalStatus = ref<string>('');
  const currentApprover = ref<boolean>(false);
  const isFinalApprover = ref<boolean>(false);
  const isCancelled = ref<boolean>(false);
  const alerts = ref<string[]>([]);
  const submitForApprovalStatus = ref<ResponseStatus>('idle');
  const allowToCurrentApprover = ref<boolean>(true);
  const paymentInfoDetails = ref<any>({});
  const enterpriseVersion = ref<boolean>(false);
  const allocationLoading = ref(false);
  const paymentsNonFcra = computed(() => {
    return sumConditional(
      paymentsDetails.value,
      (item: any) => !item.isAdvance && item.bookOfAccounts === 'Non-FCRA',
      (item: any) => +item.amount
    );
  });

  const paymentsFcra = computed(() => {
    return sumConditional(
      paymentsDetails.value,
      (item: any) => !item.isAdvance && item.bookOfAccounts === 'FCRA',
      (item: any) => +item.amount
    );
  });

  const totalPayments = computed(() => {
    return +paymentsNonFcra.value + +paymentsFcra.value;
  });

  const paymentsViaAdvancesNonFcra = computed(() => {
    return sumConditional(
      paymentsDetails.value,
      (item: any) => item.isAdvance && item.bookOfAccounts === 'Non-FCRA',
      (item: any) => +item.amount
    );
  });

  const paymentsViaAdvancesFcra = computed(() => {
    return sumConditional(
      paymentsDetails.value,
      (item: any) => item.isAdvance && item.bookOfAccounts === 'FCRA',
      (item: any) => +item.amount
    );
  });

  const totalPaymentsViaAdvances = computed(() => {
    return +paymentsViaAdvancesNonFcra.value + +paymentsViaAdvancesFcra.value;
  });

  // const totalPaymentsAndAdvancesNonFcra = computed(() => {
  //   return totalPayments.value + totalPaymentsViaAdvances.value;
  // });

  // const totalPaymentsAndAdvancesFcra = computed(() => {
  //   return totalPayments.value + totalPaymentsViaAdvances.value;
  // });

  // const totalPaymentAndAdvancesBoth = computed(() => {
  //   return +totalPaymentsAndAdvancesNonFcra.value + +totalPaymentsAndAdvancesFcra.value;
  // });

  // const balanceNonFcra = computed(() => {
  //   return paymentAllocations.value.payableNetOfTdsNonFcra - totalPaymentsAndAdvancesNonFcra.value;
  // });

  // const balanceFcra = computed(() => {
  //   return paymentAllocations.value.payableNetOfTdsFcra - totalPaymentsAndAdvancesFcra.value;
  // });

  // const balanceBoth = computed(() => {
  //   return +paymentAllocations.value.payableNetOfTds - +totalPaymentAndAdvancesBoth.value;
  // });

  const showDenyExpenseModal = () => {
    denyExpenseModal.value = true;
  };

  const hideDenyExpenseModal = () => {
    denyExpenseModal.value = false;
    // fetchData(); // Refresh data after closing modal
  };

  const showApproveExpenseModal = () => {
    approveExpenseModal.value = true;
  };

  const hideApproveExpenseModal = () => {
    approveExpenseModal.value = false;
    // fetchData(); // Refresh data after closing modal
  };

  const showCancelExpenseModal = () => {
    cancelExpenseModal.value = true;
  };

  const hideCancelExpenseModal = () => {
    cancelExpenseModal.value = false;
    fetchData();
  };

  const submitForApproval = async () => {
    submitForApprovalStatus.value = 'pending';
    const submitForApprovalResponse = await post<any>('/v1/submit_expense_for_approval', {
      data: { uid: identifier }
    });

    if (submitForApprovalResponse?.data?.status === 'success') {
      submitForApprovalStatus.value = 'success';
      openSuccessfulCustomNotification(submitForApprovalResponse?.data?.message);
      fetchData();
      return router.push(`/expenses?drawerFrom=allExpenses&identifier=${identifier}`);
    } else if (submitForApprovalResponse?.data?.status === 'error') {
      submitForApprovalStatus.value = 'error';
      openFailedCustomNotification(submitForApprovalResponse?.data?.message);
      return router.push(`/expenses?drawerFrom=allExpenses&identifier=${identifier}`);
    }
  };

  onMounted(async () => {
    const { identifier } = router.currentRoute.value.query;
    const response = await get<any>(`/v1/payment_info/${identifier}`);

    if (response?.data?.status === 'success') {
      paymentInfoDetails.value = response.data.data;
    }
  });

  const fetchData = async () => {
    responseState.value = 'pending';
    allocationLoading.value = true;
    try {
      const expenseResponse = await get<GetExpenseResponse>(`/v1/get_expense/${identifier}`);

      if (expenseResponse?.status === 200) {
        const selectedExpense = expenseResponse?.data?.data?.selected_expense;
        enterpriseVersion.value = selectedExpense?.enterprise_version;
        alerts.value = expenseResponse.data.data.alert;
        const allocationDropdownResponse = await get<AddNewExpenseAllocationDropdownApiResponse>(
          `/v1/add_new_expense/${selectedExpense.category}`
        );

        // const allocationDropdownResponse = await get<AddNewExpenseAllocationDropdownApiResponse>(
        //   `/v1/view_expense/${identifier}/${selectedExpense.category}`
        // );
        if (allocationDropdownResponse.data.status === 'success') {
          allocationLoading.value = false;
        } else if (allocationDropdownResponse.data.status === 'error') {
          allocationLoading.value = false;
        }

        const budgetOptions = allocationDropdownResponse?.data?.data?.budgets_list?.map(budget => ({
          mainLabel: budget.budget_name,
          subLabel: budget.budget_id,
          value: budget.budget_id_value,
          amount: budget.amount - (budget.utilization.approved + budget.utilization.not_approved),
          lineItem: budget.line_items.map(lineItem => ({
            label: `${lineItem.data.li} - ${lineItem.data.sub_li}`,
            value: lineItem.data.li_value,
            amount: lineItem.amount - (lineItem.utilized.approved + lineItem.utilized.not_approved)
          }))
        })) as AllocationBudgetOptions[];

        const grantOptions = allocationDropdownResponse?.data?.data?.grants_list?.map(grant => ({
          mainLabel: grant.grant_id,
          subLabel: grant.donor_name,
          value: grant.grant_id_value,
          amount: grant.amount - (grant.utilization.approved + grant.utilization.not_approved),
          purpose: grant.purpose,
          lineItem: grant.line_items.map(lineItem => ({
            label: `${lineItem.data.li} - ${lineItem.data.sub_li}`,
            value: lineItem.data.li_value,
            amount: lineItem.amount - (lineItem.utilized.approved + lineItem.utilized.not_approved)
          }))
        })) as AllocationGrantsOptions[];

        const parsedData = parseFormDataToState(selectedExpense, true, enterpriseVersion?.value);
        // We are not storing all the data that we need to show in the drawer.
        // Get other fields from the allocation dropdown response.
        parsedData.grantsAndBudgetAllocation = parsedData.grantsAndBudgetAllocation.map((item: any) => {
          const budgetOption = budgetOptions.find(budget => budget.value === item.expenseBudget);
          const grantOption = grantOptions.find(grant => grant.value === item.expenseGrants);
          const budgetSubLabel = budgetOption?.subLabel;
          const grantSubLabel = grantOption?.subLabel;
          const totalBudgetAmount = budgetOption?.amount;
          const totalGrantAmount = grantOption?.amount;

          const budgetLineItemAmount = budgetOption?.lineItem.find(
            lineItem => lineItem.value === item.expenseLineItem
          )?.amount;
          const grantLineItemAmount = grantOption?.lineItem.find(
            lineItem => lineItem.value === item.grantsLineItem
          )?.amount;
          return {
            ...item,
            budgetSubLabel,
            grantSubLabel,
            budgetLineItemAmount,
            grantLineItemAmount,
            totalBudgetAmount,
            totalGrantAmount
          };
        });
        approvalStatus.value = selectedExpense.approval_status;
        isExpenseCancelled.value = selectedExpense.is_cancelled;
        currentApprover.value = selectedExpense.is_current_approver;
        isFinalApprover.value = selectedExpense.is_final_approver;
        isCancelled.value = selectedExpense.is_cancelled;
        paymentAllocations.value = getPaymentSummary(selectedExpense.line_items);
        paymentsDetails.value = getPaymentsDetails(selectedExpense.payments);
        const status = selectedExpense?.approval_status;
        allowToCurrentApprover.value = !['Not submitted for approval', 'Approved', 'draft', '0 of 0'].includes(status)
          ? selectedExpense?.is_current_approver
          : true;
        data.value = parsedData;
        responseState.value = 'success';
      } else {
        responseState.value = 'error';
      }
    } catch (error) {
      openFailedCustomNotification('Error fetching expense details!');
      console.error('Error fetching data:', error);
    }
  };

  const isUpdatePayment = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isAdvanceCurrentApprover = currentApprover?.value;
    // const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNoApprover = approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = isCancelled?.value;

    if ((isExpenseSubmitter || isAdmin || isExpenseModuleAdmin) && isNoApprover) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter) {
      return isAdvanceCancelled ? true : false;
    }

    if (isExpenseSubmitter && !isNotSubmitted && (!isApproved || !isNoApprover)) {
      return false;
    }

    if (isExpenseModuleAdmin && !isNotSubmitted && (!isApproved || !isNoApprover)) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && !isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && (isApproved || isNoApprover)) {
      return false;
    }

    if (isExpenseApprover && isAdvanceCurrentApprover && !isNotSubmitted && (!isApproved || !isNoApprover)) {
      return false;
    }

    if (
      (isAdmin || isExpenseApprover || isExpenseModuleAdmin) &&
      isAdvanceCurrentApprover &&
      !isNotSubmitted &&
      (!isApproved || !isNoApprover)
    ) {
      return false;
    }

    if (isExpenseApprover && !isAdvanceCurrentApprover && !isNotSubmitted && (!isApproved || !isNoApprover)) {
      return false;
    }

    return true;
  });

  const isEditDisabled = computed(() => {
    const rolesList = role.value || [];
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNoApprover = approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = isCancelled?.value;
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));

    if (
      !isApproved &&
      !isNotSubmitted &&
      !isNoApprover &&
      (isExpenseModuleAdmin || isExpenseModuleAdmin) &&
      isExpenseControlAdmin
    ) {
      return false;
    }

    if ((isExpenseSubmitter || isAdmin || isExpenseModuleAdmin) && isNoApprover) {
      return false;
    }

    if (isNotSubmitted && isExpenseSubmitter && isAdvanceCancelled) {
      return false;
    }

    if (isExpenseSubmitter && isNotSubmitted && !isAdvanceCancelled) {
      return false;
    }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if (isExpenseModuleAdmin && isApproved) {
      return false;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (!isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && !isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdmin && isApproved) {
      return false;
    }

    if (isExpenseModuleAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isExpenseApprover && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isApproved && isLastApprover) {
      return false;
    }

    return true;
  });

  const isSubmitDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isAdmin = rolesList.includes('admin');
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved' || approvalStatus?.value === '0 of 0';
    const isAdvanceCancelled = isCancelled?.value;
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));

    if (isNotSubmitted && (isExpenseModuleAdmin || isExpenseModuleAdmin) && isExpenseControlAdmin) {
      return false;
    }

    if (!isNotSubmitted && !isAdvanceCancelled && !isApproved) {
      return true;
    }

    if (isNotSubmitted && isExpenseSubmitter) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isExpenseSubmitter && isApproved) {
      return true;
    }

    if (isAdmin && isNotSubmitted) {
      return false;
    }

    if (isAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover && isAdvanceCancelled) {
      return true;
    }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isAdmin) && isApproved && isLastApprover) {
      return true;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isNotSubmitted && !isAdvanceCancelled) {
      return true;
    }

    return true;
  });

  const isCancelDisabled = computed(() => {
    const rolesList = role.value || [];
    const isAdvanceCurrentApprover = currentApprover?.value;
    const isNotSubmitted = approvalStatus?.value === 'Not submitted for approval';
    const isExpenseAdmin = rolesList.includes('admin');
    const isExpenseModuleAdmin = role.value.some(r => r.endsWith('_expense_admin'));
    const isExpenseSubmitter = role.value.some(r => r.endsWith('_expense_submitter'));
    const isExpenseApprover = role.value.some(r => r.endsWith('_expense_approver'));
    const isLastApprover = isFinalApprover?.value;
    const isApproved = approvalStatus?.value === 'Approved';
    const isNotApprover = approvalStatus?.value === '0 of 0';
    const isExpenseControlAdmin = role.value.some(r => r.endsWith('_approval_control_admin'));

    if (isNotSubmitted && (isExpenseModuleAdmin || isExpenseAdmin) && isExpenseControlAdmin) {
      return false;
    }

    // if (
    //   (isExpenseAdmin || isExpenseModuleAdmin) && isExpenseControlAdmin &&
    //   !isNotSubmitted &&
    //   (isApproved || isNotApprover)
    // ) {
    //   return false;
    // }

    if (isNotSubmitted && isExpenseSubmitter) {
      return false;
    }

    if (isAdvanceCurrentApprover && isNotSubmitted && !isLastApprover) {
      return false;
    }

    if (isExpenseSubmitter && isApproved) {
      return true;
    }

    if (isExpenseSubmitter && isNotApprover) {
      return false;
    }

    if (isExpenseAdmin && isNotSubmitted) {
      return false;
    }

    // if (isExpenseAdmin && !isApproved && !isNotSubmitted && isAdvanceCurrentApprover) {
    //   return true;
    // }

    if (isExpenseModuleAdmin && isNotSubmitted) {
      return false;
    }

    if (
      (isExpenseAdmin || isExpenseApprover || isExpenseModuleAdmin) &&
      isAdvanceCurrentApprover &&
      !isNotSubmitted &&
      (!isApproved || !isNotApprover)
    ) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isExpenseAdmin) && isApproved && isLastApprover) {
      return false;
    }

    if ((isExpenseApprover || isExpenseModuleAdmin || isExpenseAdmin) && isNotSubmitted && isAdvanceCurrentApprover) {
      return false;
    }

    if (!isNotSubmitted && !isApproved && isAdvanceCurrentApprover && isExpenseApprover) {
      return false;
    }

    if (isExpenseAdmin && !isApproved && !isNotSubmitted) {
      return false;
    }

    if ((isNotSubmitted && isLastApprover) || isAdvanceCurrentApprover) {
      return true;
    }

    if (isExpenseModuleAdmin && isApproved) {
      return false;
    }

    if (isExpenseAdmin && isApproved) {
      return false;
    }

    if (isExpenseModuleAdmin && !isNotSubmitted && !isApproved) {
      return false;
    }

    return true;
  });

  onMounted(fetchData);
</script>

<template>
  <a-drawer
    v-if="identifier"
    class="monetary-drawer"
    :body-style="{ padding: '0px', paddingBottom: '38px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    title="Expense Information"
    :open="identifier !== null"
    width="1220"
    @close="onClose"
  >
    <template #extra>
      <div v-if="!isExpenseCancelled" class="flex gap-2">
        <RoleBasedButton
          component-name="cancelActionButton"
          :loading="responseState == 'pending'"
          :disabled="isCancelDisabled"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showCancelExpenseModal"
        >
          <TrashIcon class="h-4 w-4 text-red-700" />
          Cancel Expense
        </RoleBasedButton>
        <RoleBasedButton
          v-if="approvalStatus !== 'draft'"
          component-name="approveActionButton"
          :loading="responseState == 'pending'"
          :disabled="isUpdatePayment"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="
            () => navigateTo({ path: '/expenses', query: { drawerFrom: 'expensesPaymentManagement', identifier } })
          "
        >
          <PencilIcon class="h-4 w-4" />
          Update Payments
        </RoleBasedButton>
        <RoleBasedButton
          component-name="editActionButton"
          :disabled="isEditDisabled"
          :loading="submitForApprovalStatus === 'pending' || responseState == 'pending'"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-gray-500 border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="() => navigateTo({ path: '/expenses/edit-expense', query: { identifier } })"
        >
          <PencilIcon class="h-4 w-4" />
          Edit Expense
        </RoleBasedButton>
        <RoleBasedButton
          component-name="submitForApprovalButton"
          :disabled="isSubmitDisabled"
          :loading="submitForApprovalStatus === 'pending' || responseState == 'pending'"
          active-class="text-blue-700 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center text-white border border-blue-700 bg-blue-700  shadow-[0_1px_2px_0px_#0000000D]"
          disabled-class="flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-gray-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="submitForApproval"
        >
          Submit For Approval
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approveActionButton"
          :loading="submitForApprovalStatus === 'pending' || responseState == 'pending'"
          :disabled="
            approvalStatus === 'Not submitted for approval' ||
            !currentApprover ||
            (!isEmpty(Boolean(alerts)) && isFinalApprover)
          "
          active-class="!text-white hover:!text-white bg-red-700 hover:bg-red-600 disabled:!text-red-300 disabled:border-red-300 disabled:bg-red-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-red-700 hover:!border-red-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-red-300 gap-1 text-sm font-medium py-1.5 px-2 justify-center border !border-red-300 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showDenyExpenseModal"
        >
          Deny Expense
        </RoleBasedButton>
        <RoleBasedButton
          component-name="approveActionButton"
          :loading="submitForApprovalStatus === 'pending' || responseState == 'pending'"
          :disabled="
            approvalStatus === 'Not submitted for approval' ||
            !currentApprover ||
            (!isEmpty(Boolean(alerts)) && isFinalApprover)
          "
          active-class="!text-white hover:!text-white bg-green-700 hover:bg-green-600 disabled:!text-green-300 disabled:border-green-300 disabled:bg-green-50 flex rounded items-center gap-1 text-sm font-medium py-1.5 px-2 justify-center border border-green-700 hover:!border-green-600 shadow-[0_1px_3px_0px_#0000001A,0_1px_2px_0_#0000000F]"
          disabled-class="flex rounded items-center !text-green-500 gap-1 text-sm font-medium py-1.5 px-2 justify-center !bg-green-100 border !border-green-500 shadow-[0_1px_2px_0px_#0000000D]"
          @click="showApproveExpenseModal"
        >
          Approve Expense
        </RoleBasedButton>
      </div>
      <p v-else class="text-base font-bold text-red-600">Expense is Closed</p>
    </template>
    <DrawerLayout
      :response-state="responseState"
      :main-display="data.partyName"
      :sub-display="data.invoiceNumber"
      :side-display="formatToIndianCurrency(data.totalAmount)"
      @close="onClose"
    >
      <template #column1>
        <div class="p-3 flex flex-col gap-3">
          <a-alert v-if="data.approval_status === 'approved'" message="Expense is Approved" type="success" show-icon />
          <a-alert
            v-if="data.approval_status === 'pending'"
            message="Expense is Pending Approval"
            type="warning"
            show-icon
          />
          <a-alert v-if="data.approval_status === 'draft'" message="Expense is Draft" type="info" show-icon />
          <a-alert
            v-if="!allowToCurrentApprover"
            message="You are not a current approver for this expense!"
            type="error"
            show-icon
          />
          <div v-if="!isEmpty(alerts)" class="flex flex-col gap-3">
            <a-alert v-for="alert in alerts" :message="alert" type="warning" show-icon />
          </div>
        </div>
        <InfoSection>
          <InfoRow label="Expense Type" :value="data.expenseType" class="capitalize" />
          <InfoRow label="Invoice Date" :value="dayjs(data.invoiceDate).format('DD/MM/YYYY')" />
          <InfoRow label="Invoice Number" :value="data.invoiceNumber" />
          <InfoRow label="Party Name" :value="data.partyName" />
          <InfoRow label="Party bank detail" :value="data.payeeBank" />
          <InfoRow label="Description" :value="data.description" />
          <InfoRow v-if="enterpriseVersion" label="Tax Invoice" :value="data.tax_invoice ? 'Yes' : 'No'" />
        </InfoSection>
        <div>
          <GrantAndBudgetAllocationLoadingState v-if="allocationLoading" />
          <GrantsAndBudgetAllocation v-else :allocations="data.grantsAndBudgetAllocation" />
        </div>
      </template>
      <template #column2>
        <Attachments :items="data?.attachments" :table="data?.table" />
        <AuditLogs :items="data?.auditLogs" title="Expense" />
        <Approvals
          :items="data?.approversList"
          :creator="data?.creator.email"
          :draft-status="data?.draftStatus"
          status-of="expense"
        />
      </template>
    </DrawerLayout>

    <a-collapse
      v-model:active-key="activeKey"
      class="fixed bottom-0 right-0 w-[1220px] paymentCollapse"
      :default-active-key="['1']"
    >
      <template #expandIcon="{ isActive }">
        <ChevronDownIcon class="text-black w-4 h-4" :class="{ 'rotate-180': !isActive }" />
      </template>
      <a-collapse-panel key="1" header="Payment Summary" class="">
        <div class="rounded shadow-[0_1px_4px_0px_#0C0C0D0D] overflow-hidden">
          <table class="w-full">
            <thead>
              <tr class="bg-gray-50">
                <td class="border border-gray-200 w-full"></td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">
                  Non-FCRA
                </td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">FCRA</td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">
                  Unallocated
                </td>
                <td class="border border-gray-200 xs-medium text-gray-600 min-w-[140px] px-3 py-2 text-right">Total</td>
              </tr>
            </thead>
            <tbody>
              <PaymentSummaryRow
                :expense-details="[
                  'Total Expense Allocation',
                  paymentAllocations.totalExpenseAllocationNonFcra,
                  paymentAllocations.totalExpenseAllocationFcra,
                  paymentAllocations.totalUnallocatedTotalAmount,
                  paymentAllocations.totalExpenseAllocation
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'TDS',
                  paymentAllocations.tdsNonFcra,
                  paymentAllocations.tdsFcra,
                  paymentAllocations.unallocatedTdsAmount,
                  paymentAllocations.totalTdsAmount
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Payable (Net of TDS) - (A)',
                  paymentAllocations.payableNetOfTdsNonFcra,
                  paymentAllocations.payableNetOfTdsFcra,
                  paymentAllocations.payableNetOfTdsUnallocated,
                  paymentAllocations.payableNetOfTds
                ]"
              />
              <PaymentSummaryRow :expense-details="['Payments', paymentsNonFcra, paymentsFcra, '-', totalPayments]" />
              <PaymentSummaryRow
                :expense-details="[
                  'Payments via Advances',
                  paymentsViaAdvancesNonFcra,
                  paymentsViaAdvancesFcra,
                  '-',
                  totalPaymentsViaAdvances
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Total Amount Paid - (B)',
                  paymentInfoDetails?.total_advance_payment_with_payment_for_nonfcra,
                  paymentInfoDetails?.total_advance_payment_with_payment_for_fcra,
                  '-',
                  paymentInfoDetails?.total_advance_payment_with_payment_both
                ]"
              />
              <PaymentSummaryRow
                :expense-details="[
                  'Balance',
                  paymentInfoDetails.total_balance_with_nonfcra,
                  paymentInfoDetails.total_balance_with_fcra,
                  paymentInfoDetails.total_unallocated,
                  paymentInfoDetails.balanceBoth
                ]"
              />
            </tbody>
          </table>
        </div>
      </a-collapse-panel>
    </a-collapse>
  </a-drawer>
  <CancelExpenseModal :visible="cancelExpenseModal" :close="hideCancelExpenseModal" :expense-id="identifier" />
  <DenyExpenseModal :visible="denyExpenseModal" :close="hideDenyExpenseModal" :expense-id="identifier" />
  <ApproveExpenseModal :visible="approveExpenseModal" :close="hideApproveExpenseModal" :expense-id="identifier" />
</template>

<style>
  @import '~/assets/css/donationDrawer.css';
</style>
